import React, { useEffect, useState, useCallback } from 'react';
import { Activity } from 'lucide-react';
import { homeApi } from '@/utils/api';
import { getAny, setCached } from '@/utils/cache';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useTranslation } from 'react-i18next';

const POLL_MS = 15000;

interface AddedPoint {
    date: string;
    label: string;
    count: number;
}

interface ListingsStats {
    success?: boolean;
    totalListings: number;
    listingBreakdown?: { store: number; offer: number; auction: number };
    listingHistory?: AddedPoint[];
    ts?: number;
}

const EMPTY: ListingsStats = {
    totalListings: 0,
    listingBreakdown: { store: 0, offer: 0, auction: 0 },
    listingHistory: [],
};

// ─── iOS-style spring count-up ───
function useAnimatedNumber(target: number, duration = 800): number {
    const [display, setDisplay] = useState(target);
    const fromRef = React.useRef(target);

    useEffect(() => {
        const from = fromRef.current;
        const to = Number.isFinite(target) ? target : 0;
        if (from === to) return;
        fromRef.current = to;
        const start = performance.now();
        let raf = 0;
        const step = (now: number) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(from + (to - from) * eased);
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
}

// ─── Smooth line path (Catmull-Rom → cubic bezier) ───
const buildSmoothPath = (pts: [number, number][]) => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0][0]},${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];
        const c1x = p1[0] + (p2[0] - p0[0]) / 6;
        const c1y = p1[1] + (p2[1] - p0[1]) / 6;
        const c2x = p2[0] - (p3[0] - p1[0]) / 6;
        const c2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
    }
    return d;
};

// ─── Glowing live dot ───
const LiveDot = () => (
    <span className="relative flex h-[5px] w-[5px] shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-70" />
        <span className="relative inline-flex rounded-full h-[5px] w-[5px] bg-[#34C759] shadow-[0_0_6px_2px_rgba(52,199,89,0.5)]" />
    </span>
);

export const UserGrowthCard: React.FC = () => {
    const { impactOccurred } = useHapticFeedback();
    const { t } = useTranslation(['common', 'home']);
    const [stats, setStats] = useState<ListingsStats>(() => getAny<ListingsStats>('user_gauge_stats') || EMPTY);
    const [connected, setConnected] = useState(!!getAny('user_gauge_stats'));
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        let alive = true;

        const load = async () => {
            try {
                const res = await homeApi.getUserStats();
                const data = await res.json();
                if (!alive) return;
                if (data?.success) {
                    setStats(data);
                    setCached('user_gauge_stats', data);
                    setConnected(true);
                }
            } catch (err) {
                console.error('[UserGrowthCard] fetch failed:', err);
                if (alive) setConnected(false);
            }
        };

        load();
        const id = setInterval(load, POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const total = stats.totalListings || 0;
    const bd = stats.listingBreakdown || { store: 0, offer: 0, auction: 0 };
    const aTotal = useAnimatedNumber(total);

    const series: AddedPoint[] =
        stats.listingHistory && stats.listingHistory.length ? stats.listingHistory : [];
    const counts = series.map(p => p.count);
    const maxV = counts.length ? Math.max(...counts, 1) : 1;

    // ── Apple Watch-style waveform (√-scale lifts low values, keeps peaks) ──
    const W = 132;
    const H = 44;
    const TOP = 4;
    const BOTTOM = 12;
    const span = H - TOP - BOTTOM;
    const pts: [number, number][] = counts.map((c, i) => [
        TOP + (i / Math.max(counts.length - 1, 1)) * (W - TOP * 2),
        TOP + (1 - (c > 0 ? Math.pow(Math.min(c / maxV, 1), 0.4) : 0.12)) * span,
    ]);
    const baseY = TOP + span;
    const linePath = buildSmoothPath(pts);
    const areaPath = pts.length >= 2
        ? `${linePath} L ${pts[pts.length - 1][0]},${baseY} L ${pts[0][0]},${baseY} Z`
        : '';
    const last = pts[pts.length - 1] || [TOP, TOP + span];

    // x-axis date ticks (4, aligned to points)
    const n = Math.max(counts.length - 1, 1);
    const tickIdx = counts.length ? [0, Math.round(n / 3), Math.round((2 * n) / 3), n] : [];
    const tickEls = tickIdx.map((ix, j) => {
        const p = series[ix];
        const x = TOP + (ix / n) * (W - TOP * 2);
        return (
            <div key={j} className="absolute bottom-0 font-mono text-[7px] text-[#707070]" style={{ left: `${(x / W) * 100}%`, transform: 'translateX(-50%)' }}>
                {p?.label ?? ''}
            </div>
        );
    });

    const clock = new Date(now).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).toUpperCase();
    const dateStr = new Date(now).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        weekday: 'short',
    });

    const handlePress = useCallback(() => impactOccurred?.('light'), [impactOccurred]);

    return (
        <div className="select-none flex-1 min-w-0 w-full" onPointerDown={handlePress}>
            <div
                className="relative w-full rounded-[20px] glass-strong border border-white/5"
                style={{ boxShadow: '0 14px 30px -18px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)' }}
            >
                {/* ── Glass sheen ── */}
                <div className="absolute inset-0 rounded-[20px] pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0) 26%)' }} />

                {/* ── Glowing corner brackets ── */}
                <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 40%, transparent 65%)' }} />
                <div
                    className="absolute top-0 left-0 w-9 h-9 rounded-tl-[20px] pointer-events-none"
                    style={{ borderTop: '1.5px solid rgba(255,255,255,0.92)', borderLeft: '1.5px solid rgba(255,255,255,0.92)', filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' }}
                />
                <div className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 40%, transparent 65%)' }} />
                <div
                    className="absolute bottom-0 right-0 w-9 h-9 rounded-br-[20px] pointer-events-none"
                    style={{ borderBottom: '1.5px solid rgba(255,255,255,0.92)', borderRight: '1.5px solid rgba(255,255,255,0.92)', filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' }}
                />

                {/* ── Content ── */}
                <div className="relative z-10 flex flex-col items-center px-3 pt-3.5 pb-2.5 portals-font">
                    {/* header: live dot + date */}
                    <div className="relative w-full flex items-center justify-center">
                        <div className="flex items-center gap-1">
                            <LiveDot />
                            <span className={`text-[9px] tracking-wide ${connected ? 'text-[#707070]' : 'text-[#555]'}`}>
                                {dateStr}
                            </span>
                        </div>
                        <Activity className="absolute right-0 top-0 w-3 h-3 text-[#707070]" strokeWidth={1.75} />
                    </div>

                    {/* main: store listings */}
                    <div className="mt-2 flex items-end justify-center">
                        <span className="portals-numeric text-[30px] leading-none font-extralight text-[#E0E0E0] tracking-tight">
                            {aTotal.toLocaleString('en-US')}
                        </span>
                    </div>
                    <span className="mt-1 text-[8px] font-semibold uppercase tracking-[0.2em] text-[#555]">
                        Listings · {clock}
                    </span>

                    {/* offer / auction / store (real listing stats) */}
                    <div className="mt-2.5 flex items-start justify-center gap-4">
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-[#E17055]" />
                                <span className="text-[8px] font-semibold text-[#707070] tracking-[0.14em]">{String(t('common:offer', 'OFFER'))}</span>
                            </div>
                            <span className="portals-numeric text-[11px] font-normal text-[#E0E0E0]">{bd.offer}</span>
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-[#A78BFA]" />
                                <span className="text-[8px] font-semibold text-[#707070] tracking-[0.14em]">{String(t('common:auct', 'AUCT'))}</span>
                            </div>
                            <span className="portals-numeric text-[11px] font-normal text-[#E0E0E0]">{bd.auction}</span>
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-[#34C759]" />
                                <span className="text-[8px] font-semibold text-[#707070] tracking-[0.14em]">{String(t('common:store', 'STORE'))}</span>
                            </div>
                            <span className="portals-numeric text-[11px] font-normal text-[#E0E0E0]">{bd.store}</span>
                        </div>
                    </div>

                    {/* Apple Watch-style line chart + real dates */}
                    <div className="w-full mt-3">
                        <svg viewBox={`0 0 ${W} ${H}`} className="w-full block" style={{ height: H }}>
                            <defs>
                                <linearGradient id="ugLineGrad" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#34F5C5" />
                                    <stop offset="50%" stopColor="#3390EC" />
                                    <stop offset="100%" stopColor="#A78BFA" />
                                </linearGradient>
                                <linearGradient id="ugAreaGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="rgba(51,144,236,0.28)" />
                                    <stop offset="100%" stopColor="rgba(51,144,236,0)" />
                                </linearGradient>
                            </defs>
                            {areaPath && <path d={areaPath} fill="url(#ugAreaGrad)" />}
                            {linePath && (
                                <path
                                    d={linePath}
                                    fill="none"
                                    stroke="url(#ugLineGrad)"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ filter: 'drop-shadow(0 0 3px rgba(51,144,236,0.55))' }}
                                />
                            )}
                            <circle cx={last[0]} cy={last[1]} r="2.2" fill="#34F5C5" />
                        </svg>
                        {/* timeline (separate row, below the wave) */}
                        <div className="relative h-[10px] mt-1">
                            {tickEls}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserGrowthCard;